import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { API } from "../App";

// Generates the certificates ZIP of one list via background job
// (avoids the 120s proxy timeout on big lists) and triggers the
// browser download. Returns { ok, certFormat }.
async function runCertJobAndDownload(listId, courseKey, extraQuery = "") {
  const start = await axios.post(
    `${API}/admin/courses/lists/${listId}/certificates-async${extraQuery}`,
  );
  const jobId = start.data.job_id;
  const deadline = Date.now() + 15 * 60 * 1000;
  let job = null;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2500));
    const st = await axios.get(`${API}/admin/courses/jobs/${jobId}`);
    job = st.data;
    if (job.status === "done") break;
    if (job.status === "error") {
      throw new Error(job.error || "Erro ao gerar certificados");
    }
  }
  if (!job || job.status !== "done") {
    throw new Error("Tempo esgotado ao gerar certificados — tente novamente");
  }
  const res = await axios.get(
    `${API}/admin/courses/jobs/${jobId}/download`,
    { responseType: "blob", timeout: 300000 },
  );
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = job.filename || `certificados_${courseKey}_${listId.slice(0, 8)}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return { ok: true, certFormat: (job.cert_format || "").toLowerCase() };
}
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  GraduationCap,
  Users,
  Download,
  ListPlus,
  Trash2,
  Loader2,
  Calendar,
  Clock,
  MapPin,
  RefreshCw,
  History,
  Search,
  CheckCircle2,
  AlertCircle,
  Award,
  Upload,
  Mail,
  Filter,
  FileSpreadsheet,
  PieChart as PieChartIcon,
  Pause,
  Play,
  ScanFace,
  FileCheck2,
} from "lucide-react";
import { QuestionarioReportDialog } from "./QuestionarioReportDialog";
import { FaceReviewDialog } from "./FaceReviewDialog";
import { SignatureReceiptsDialog } from "./SignatureReceiptsDialog";
import { ListParticipantsDialog } from "./ListParticipantsDialog";

const CourseCard = ({ course, onGenerate, onHistory }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
  >
    <Card className="border-slate-200 hover:border-slate-300 transition-colors h-full">
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 leading-tight">
              {course.titulo}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
              {course.tema}
            </p>
          </div>
        </div>

        <div className="space-y-1.5 text-xs text-slate-600 mb-4">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <span>{course.data}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>
              {course.horario} • {course.carga_horaria}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-slate-400" />
            <span className="truncate">{course.local}</span>
          </div>
        </div>

        <div className="mt-auto">
          <div className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <History className="w-4 h-4 text-slate-500" />
              <span>Listas geradas</span>
            </div>
            <span
              className="text-base font-semibold text-slate-900"
              data-testid={`admin-course-lists-count-${course.course_key}`}
            >
              {course.lists_count ?? 0}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onHistory(course)}
              data-testid={`admin-course-history-${course.course_key}`}
            >
              <History className="w-4 h-4 mr-1" />
              Histórico
            </Button>
            <Button
              size="sm"
              onClick={() => onGenerate(course)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid={`admin-course-generate-${course.course_key}`}
            >
              <ListPlus className="w-4 h-4 mr-1" />
              Gerar Lista
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

/* -------------------- Generate List Dialog (employee picker) -------------------- */
const SupervisorAutocompleteInput = ({
  value,
  onChange,
  onSelect,
  onCommit,
  placeholder,
  disabled,
  saving,
  options,
  testIdInput,
  testIdDropdown,
  optionTestIdPrefix,
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const updatePos = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left, top: r.bottom + 4, width: r.width });
  };

  useEffect(() => {
    if (!open) return;
    updatePos();
    const handler = () => updatePos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  const matches =
    open && (value || "").trim().length >= 2 ? options.slice(0, 8) : [];

  return (
    <>
      <Input
        ref={inputRef}
        value={value}
        onChange={(ev) => {
          onChange(ev.target.value);
          setOpen(true);
          updatePos();
        }}
        onFocus={() => {
          setOpen(true);
          updatePos();
        }}
        onBlur={() => {
          // Delay close so click on dropdown registers.
          setTimeout(() => {
            setOpen(false);
            onCommit?.();
          }, 180);
        }}
        placeholder={placeholder}
        className="h-8 text-sm flex-1"
        disabled={disabled}
        data-testid={testIdInput}
      />
      {saving && (
        <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
      )}
      {open &&
        pos &&
        (value || "").trim().length >= 2 &&
        createPortal(
          <div
            className="fixed z-[10000] max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-xl"
            style={{
              left: pos.left,
              top: pos.top,
              width: Math.max(pos.width, 260),
              pointerEvents: "auto",
            }}
            data-testid={testIdDropdown}
          >
            {matches.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">
                Nenhum funcionário encontrado.
              </div>
            ) : (
              matches.map((cand) => (
                <button
                  type="button"
                  key={cand.id}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    onSelect(cand);
                    setOpen(false);
                  }}
                  data-testid={`${optionTestIdPrefix}${cand.matricula}`}
                >
                  <span className="font-mono text-[11px] text-blue-600">
                    {cand.matricula}
                  </span>
                  <span className="text-slate-900 truncate">
                    {cand.nome}
                  </span>
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

const NUM_SLOTS = 10;

const makeBlankSlot = () => ({
  importResult: null,
  selectedIds: {},
  trainingDate: "",
  horario: "",
  // All new course lists must use digital signature (OTP) — the
  // manual "rubrica" flow is no longer offered to admins.
  mode: "digital",
  expiresAt: "",
  importing: false,
  saving: false,
  savedResult: null,
  saveError: null,
});

const GenerateListDialog = ({ open, onOpenChange, course, onCreated }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState(() =>
    Array.from({ length: NUM_SLOTS }, makeBlankSlot)
  );
  const [openSlotIdx, setOpenSlotIdx] = useState(0);
  const [generatingAll, setGeneratingAll] = useState(false);
  // CIPA-only: per-employee in-progress supervisor edits + save state
  const [supervisorEdits, setSupervisorEdits] = useState({});
  const [savingSupervisorId, setSavingSupervisorId] = useState(null);
  // Manual employee picker per slot (collapsed by default)
  const [manualOpenBySlot, setManualOpenBySlot] = useState({});
  const [manualQueryBySlot, setManualQueryBySlot] = useState({});
  // Per-slot file input refs
  const fileRefs = useRef([]);

  const isCipa = course?.course_key === "cipa";

  useEffect(() => {
    if (!open) return;
    // Default deadline: now + 7 days in YYYY-MM-DDTHH:mm (local)
    const dt = new Date(Date.now() + 7 * 24 * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    const defaultExpires = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
      dt.getDate()
    )}T23:59`;
    // Reset all slots; pre-fill course default date + default expiresAt em todos
    setSlots(
      Array.from({ length: NUM_SLOTS }, () => ({
        ...makeBlankSlot(),
        trainingDate: course?.data || "",
        expiresAt: defaultExpires,
      }))
    );
    setOpenSlotIdx(0);
    setSupervisorEdits({});
    setManualOpenBySlot({});
    setManualQueryBySlot({});
    // Load employees (used for CIPA supervisor autocomplete + record lookup)
    setLoading(true);
    axios
      .get(`${API}/admin/employees-for-list`, { timeout: 30000 })
      .then((res) => setEmployees(res.data || []))
      .catch(() => toast.error("Erro ao carregar funcionários"))
      .finally(() => setLoading(false));
  }, [open, course]);

  // Map id -> employee record (for supervisor editing & lookup)
  const empById = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      m[e.id] = e;
    });
    return m;
  }, [employees]);

  // Set of employee IDs already selected in slots OTHER than `excludeIdx`.
  const usedIdsExcept = useCallback(
    (excludeIdx) => {
      const s = new Set();
      slots.forEach((slot, idx) => {
        if (idx === excludeIdx) return;
        Object.entries(slot.selectedIds || {}).forEach(([id, v]) => {
          if (v) s.add(id);
        });
      });
      return s;
    },
    [slots]
  );

  const updateSlot = (idx, patch) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    );
  };

  const handleXlsxImportForSlot = async (idx, file) => {
    if (!file) return;
    // Rubrica só é exigida no modo manual (presencial); listas digitais
    // assinam via OTP e dispensam a rubrica salva.
    const requireRubrica = slots[idx]?.mode === "manual";
    updateSlot(idx, { importing: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/admin/employees/match-from-xlsx`,
        fd,
        { timeout: 60000 }
      );
      const { matched = [], not_found = [], stats } = res.data || {};
      const usedElsewhere = usedIdsExcept(idx);
      // Auto-select matched-with-signature that aren't already in another slot
      const newSelected = {};
      let conflicts = 0;
      matched.forEach((m) => {
        if (!requireRubrica || m.has_signature) {
          if (usedElsewhere.has(m.id)) {
            conflicts += 1;
          } else {
            newSelected[m.id] = true;
          }
        }
      });
      updateSlot(idx, {
        importResult: { matched, not_found, stats, conflicts },
        selectedIds: newSelected,
      });
      const ok = Object.keys(newSelected).length;
      toast.success(
        conflicts > 0
          ? `Slot ${idx + 1}: ${ok} selecionado(s) — ${conflicts} já alocado(s) em outro slot.`
          : `Slot ${idx + 1}: ${ok} funcionário(s) selecionado(s).`
      );
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Falha ao importar planilha";
      toast.error(`Slot ${idx + 1}: ${detail}`);
    } finally {
      updateSlot(idx, { importing: false });
      const ref = fileRefs.current[idx];
      if (ref) ref.value = "";
    }
  };

  const toggleSlotMatch = (idx, empId) => {
    setSlots((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const next = { ...s.selectedIds };
        if (next[empId]) delete next[empId];
        else next[empId] = true;
        return { ...s, selectedIds: next };
      })
    );
  };

  const clearSlot = (idx) => {
    setSlots((prev) =>
      prev.map((s, i) =>
        i === idx ? { ...makeBlankSlot(), expiresAt: s.expiresAt } : s
      )
    );
  };

  const saveSupervisor = async (emp, overrideValue) => {
    const value = (
      overrideValue !== undefined
        ? overrideValue
        : supervisorEdits[emp.id] ?? emp.supervisor ?? ""
    ).trim();
    if ((emp.supervisor || "") === value) return;
    setSavingSupervisorId(emp.id);
    try {
      await axios.put(
        `${API}/admin/employee/${emp.id}/supervisor`,
        { supervisor: value },
        { timeout: 15000 }
      );
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === emp.id ? { ...e, supervisor: value || null } : e
        )
      );
      setSupervisorEdits((prev) => {
        const next = { ...prev };
        delete next[emp.id];
        return next;
      });
      toast.success("Supervisor salvo");
    } catch {
      toast.error("Erro ao salvar supervisor");
    } finally {
      setSavingSupervisorId(null);
    }
  };

  // Slots ready to emit: have at least one selected & not saved yet
  const slotsReady = slots
    .map((s, idx) => ({ slot: s, idx }))
    .filter(
      ({ slot }) =>
        !slot.savedResult && Object.values(slot.selectedIds).some(Boolean)
    );

  const slotsSavedCount = slots.filter((s) => s.savedResult).length;

  const isOrdemServico = course?.course_key === "ordem_servico";
  const isDeclaracao = course?.course_key === "declaracao_act";

  const validateSlot = (slot, idx) => {
    const ids = Object.entries(slot.selectedIds)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (ids.length === 0)
      return `Slot ${idx + 1}: nenhum funcionário selecionado.`;
    // Ordem de Serviço uses the employee's admission date as the
    // document date — no manual date input required.
    if (!isOrdemServico && !slot.trainingDate && !course?.data)
      return `Slot ${idx + 1}: data do treinamento ausente.`;
    return null;
  };

  const handleGenerateAll = async () => {
    if (slotsReady.length === 0) {
      toast.error("Nenhum slot configurado para emissão.");
      return;
    }
    for (const { slot, idx } of slotsReady) {
      const err = validateSlot(slot, idx);
      if (err) {
        toast.error(err);
        setOpenSlotIdx(idx);
        return;
      }
    }
    setGeneratingAll(true);
    let okCount = 0;
    let errCount = 0;
    for (const { slot, idx } of slotsReady) {
      updateSlot(idx, { saving: true, saveError: null });
      const ids = Object.entries(slot.selectedIds)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const body = {
        employee_ids: ids,
        training_date: slot.trainingDate || course.data,
      };
      if (slot.horario) body.horario = slot.horario;
      // 2026-07-09: admin now picks the mode per emission again. Send
      // it explicitly so the backend applies the right flow (OTP vs
      // rubrica). ``expires_at`` only makes sense on the OTP flow.
      body.mode = slot.mode === "manual" ? "manual" : "digital";
      if (body.mode === "digital" && slot.expiresAt) {
        body.expires_at = new Date(slot.expiresAt).toISOString();
      }
      try {
        const res = await axios.post(
          `${API}/admin/courses/${course.course_key}/lists`,
          body,
          { timeout: 60000 }
        );
        updateSlot(idx, {
          saving: false,
          savedResult: {
            id: res.data.id,
            count: res.data.count,
            sms_dispatched: res.data.sms_dispatched,
            email_dispatched: res.data.email_dispatched,
            sem_email: res.data.sem_email || [],
            mode: slot.mode,
          },
        });
        if ((res.data.sem_email || []).length > 0) {
          toast.warning(
            `Sem e-mail cadastrado: ${res.data.sem_email.join(", ")}`,
            { duration: 10000 }
          );
        }
        okCount += 1;
        onCreated?.(res.data);
      } catch (e) {
        const detail =
          e?.response?.data?.detail || "Erro ao criar lista";
        updateSlot(idx, { saving: false, saveError: detail });
        errCount += 1;
        toast.error(`Slot ${idx + 1}: ${detail}`);
      }
    }
    setGeneratingAll(false);
    if (errCount === 0) {
      toast.success(`${okCount} lista(s) gerada(s) com sucesso.`);
      onOpenChange(false);
    } else {
      toast.success(`${okCount} sucesso, ${errCount} com erro.`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-slate-700" />
            Gerar Listas — {course?.titulo}
          </DialogTitle>
          <DialogDescription>
            Configure até 10 listas em paralelo. Cada slot importa sua própria
            planilha, tem sua data e horário. Você escolhe se envia a aula
            (OTP + quiz + certificado no fim) ou se o treinamento foi
            presencial (certificado emitido na hora com rubrica).
          </DialogDescription>
        </DialogHeader>

        <div
          className="overflow-y-auto pr-1 flex-1 space-y-2"
          data-testid="generate-list-slots-container"
        >
          {loading && (
            <div className="py-3 flex items-center justify-center text-xs text-slate-500">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Carregando funcionários...
            </div>
          )}
          {slots.map((slot, idx) => {
            const isOpen = openSlotIdx === idx;
            const selectedCount = Object.values(slot.selectedIds).filter(
              Boolean
            ).length;
            const noSig =
              slot.mode === "manual"
                ? (slot.importResult?.matched || []).filter(
                    (m) => !m.has_signature
                  )
                : [];
            const isSaved = !!slot.savedResult;
            const hasError = !!slot.saveError;

            return (
              <div
                key={idx}
                className={`rounded-lg border transition ${
                  isSaved
                    ? "border-emerald-300 bg-emerald-50"
                    : hasError
                    ? "border-rose-300 bg-rose-50"
                    : isOpen
                    ? "border-slate-400 bg-white shadow-sm"
                    : "border-slate-200 bg-white"
                }`}
                data-testid={`generate-list-slot-${idx}`}
              >
                {/* Slot header — accordion toggle */}
                <button
                  type="button"
                  onClick={() => setOpenSlotIdx(isOpen ? -1 : idx)}
                  className="w-full px-3 py-2.5 flex items-center gap-3 text-left"
                  data-testid={`generate-list-slot-header-${idx}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                      isSaved
                        ? "bg-emerald-600 text-white"
                        : selectedCount > 0
                        ? "bg-slate-900 text-white"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">
                        Lista {idx + 1}
                      </span>
                      {isSaved ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Gerada — {slot.savedResult.count} participante(s)
                          {slot.savedResult.mode === "digital" &&
                            slot.savedResult.sms_dispatched != null && (
                              <span className="ml-1">
                                • {slot.savedResult.sms_dispatched} SMS/WhatsApp
                                {slot.savedResult.email_dispatched > 0 &&
                                  ` • ${slot.savedResult.email_dispatched} e-mail(s)`}
                              </span>
                            )}
                          {slot.savedResult.mode === "digital" &&
                            (slot.savedResult.sem_email || []).length > 0 && (
                              <span
                                className="ml-1 text-amber-700"
                                data-testid={`slot-sem-email-warning-${idx}`}
                              >
                                • Sem e-mail:{" "}
                                {slot.savedResult.sem_email.join(", ")}
                              </span>
                            )}
                          {slot.savedResult.dispatch_background && (
                            <span className="ml-1 text-sky-700">
                              • convocações sendo enviadas em segundo plano
                            </span>
                          )}
                          {slot.savedResult.mode === "digital" &&
                            (slot.savedResult.sem_contato || []).length > 0 && (
                              <span
                                className="ml-1 text-rose-700"
                                data-testid={`slot-sem-contato-${idx}`}
                              >
                                • {slot.savedResult.sem_contato.length} sem
                                contato (verão apenas no portal){" "}
                                <button
                                  type="button"
                                  className="underline font-semibold"
                                  data-testid={`slot-sem-contato-xlsx-${idx}`}
                                  onClick={(ev) => {
                                    ev.stopPropagation();
                                    const rows = slot.savedResult.sem_contato.map(
                                      (p) => ({
                                        NOME: p.nome,
                                        MATRICULA: p.matricula,
                                        POSTO: p.posto_servico,
                                      })
                                    );
                                    const ws = XLSX.utils.json_to_sheet(rows);
                                    ws["!cols"] = [
                                      { wch: 42 }, { wch: 12 }, { wch: 28 },
                                    ];
                                    const wb = XLSX.utils.book_new();
                                    XLSX.utils.book_append_sheet(
                                      wb, ws, "Sem contato"
                                    );
                                    XLSX.writeFile(
                                      wb,
                                      "funcionarios_sem_contato.xlsx"
                                    );
                                  }}
                                >
                                  Baixar Excel
                                </button>
                              </span>
                            )}
                        </span>
                      ) : hasError ? (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-700 truncate">
                          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          {slot.saveError}
                        </span>
                      ) : selectedCount > 0 ? (
                        <span className="text-xs text-slate-600">
                          {selectedCount} funcionário(s)
                          {slot.trainingDate && ` • ${slot.trainingDate}`}
                          {slot.horario && ` • ${slot.horario}`}
                          {slot.mode === "digital" ? (
                            <span className="ml-1 text-emerald-700 font-semibold">
                              • Digital
                            </span>
                          ) : (
                            <span className="ml-1 text-indigo-700 font-semibold">
                              • Rubrica
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">
                          Vazio — clique para configurar
                        </span>
                      )}
                    </div>
                  </div>
                  {!isSaved && selectedCount > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSlot(idx);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          clearSlot(idx);
                        }
                      }}
                      className="text-xs text-slate-500 hover:text-rose-600 px-1.5 cursor-pointer"
                      data-testid={`generate-list-slot-clear-${idx}`}
                    >
                      Limpar
                    </span>
                  )}
                  <svg
                    className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Slot body — only when expanded and not saved */}
                {isOpen && !isSaved && (
                  <div className="px-3 pb-3 border-t border-slate-100 pt-3 space-y-3">
                    {/* Ordem de Serviço → escolha explícita OTP vs Rubrica
                        (não é uma "aula", é um documento).
                        Cursos EAD → pergunta única "Enviar aula?":
                          • SIM (default) → aula + OTP + quiz + certificado no fim
                          • NÃO           → treinamento presencial já feito;
                            certificado emitido AGORA com rubrica cadastrada. */}
                    {isOrdemServico ? (
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1.5">
                          Modo de assinatura
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => updateSlot(idx, { mode: "digital" })}
                            data-testid={`slot-${idx}-mode-digital`}
                            className={
                              "rounded-md border p-2 text-left transition-colors flex items-start gap-2 " +
                              (slot.mode === "digital"
                                ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-500"
                                : "border-slate-200 bg-white hover:border-slate-300")
                            }
                          >
                            <svg
                              className={
                                "w-4 h-4 mt-0.5 shrink-0 " +
                                (slot.mode === "digital"
                                  ? "text-emerald-700"
                                  : "text-slate-400")
                              }
                              fill="none" stroke="currentColor"
                              strokeWidth="2" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round"
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <div className="min-w-0">
                              <div className={
                                "text-xs font-semibold " +
                                (slot.mode === "digital"
                                  ? "text-emerald-700"
                                  : "text-slate-600")
                              }>Digital (OTP)</div>
                              <div className="text-[10px] text-slate-500 leading-tight">
                                Envio via WhatsApp / SMS / E-mail com código OTP.
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => updateSlot(idx, { mode: "manual" })}
                            data-testid={`slot-${idx}-mode-manual`}
                            className={
                              "rounded-md border p-2 text-left transition-colors flex items-start gap-2 " +
                              (slot.mode === "manual"
                                ? "border-indigo-600 bg-indigo-50 ring-1 ring-indigo-500"
                                : "border-slate-200 bg-white hover:border-slate-300")
                            }
                          >
                            <svg
                              className={
                                "w-4 h-4 mt-0.5 shrink-0 " +
                                (slot.mode === "manual"
                                  ? "text-indigo-700"
                                  : "text-slate-400")
                              }
                              fill="none" stroke="currentColor"
                              strokeWidth="2" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            <div className="min-w-0">
                              <div className={
                                "text-xs font-semibold " +
                                (slot.mode === "manual"
                                  ? "text-indigo-700"
                                  : "text-slate-600")
                              }>Rubrica</div>
                              <div className="text-[10px] text-slate-500 leading-tight">
                                Estampa a rubrica cadastrada de cada funcionário no PDF.
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    ) : isDeclaracao ? (
                      <div
                        className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-[11px] text-emerald-900 leading-snug"
                        data-testid={`slot-${idx}-decl-digital-info`}
                      >
                        <b>Assinatura Digital (OTP).</b> Cada funcionário
                        receberá a convocação (WhatsApp / SMS / E-mail e
                        portal), poderá <b>ler a declaração personalizada</b>{" "}
                        no aplicativo antes de assinar com código OTP. A AVI
                        assina com certificado ICP-Brasil.
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1.5">
                          Enviar aula ao funcionário?
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => updateSlot(idx, { mode: "digital" })}
                            data-testid={`slot-${idx}-send-class-yes`}
                            className={
                              "rounded-md border p-2 text-left transition-colors flex items-start gap-2 " +
                              (slot.mode === "digital"
                                ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-500"
                                : "border-slate-200 bg-white hover:border-slate-300")
                            }
                          >
                            <svg
                              className={
                                "w-4 h-4 mt-0.5 shrink-0 " +
                                (slot.mode === "digital"
                                  ? "text-emerald-700"
                                  : "text-slate-400")
                              }
                              fill="none" stroke="currentColor"
                              strokeWidth="2" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div className="min-w-0">
                              <div className={
                                "text-xs font-semibold " +
                                (slot.mode === "digital"
                                  ? "text-emerald-700"
                                  : "text-slate-600")
                              }>Sim — enviar aula</div>
                              <div className="text-[10px] text-slate-500 leading-tight">
                                Convocação por OTP → funcionário assiste vídeo → responde quiz → certificado no fim.
                              </div>
                            </div>
                          </button>

                          <button
                            type="button"
                            onClick={() => updateSlot(idx, { mode: "manual" })}
                            data-testid={`slot-${idx}-send-class-no`}
                            className={
                              "rounded-md border p-2 text-left transition-colors flex items-start gap-2 " +
                              (slot.mode === "manual"
                                ? "border-indigo-600 bg-indigo-50 ring-1 ring-indigo-500"
                                : "border-slate-200 bg-white hover:border-slate-300")
                            }
                          >
                            <svg
                              className={
                                "w-4 h-4 mt-0.5 shrink-0 " +
                                (slot.mode === "manual"
                                  ? "text-indigo-700"
                                  : "text-slate-400")
                              }
                              fill="none" stroke="currentColor"
                              strokeWidth="2" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round"
                                d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-2a4 4 0 100-8 4 4 0 000 8zm6 0a3 3 0 100-6 3 3 0 000 6zM3 8a3 3 0 106 0 3 3 0 00-6 0z" />
                            </svg>
                            <div className="min-w-0">
                              <div className={
                                "text-xs font-semibold " +
                                (slot.mode === "manual"
                                  ? "text-indigo-700"
                                  : "text-slate-600")
                              }>Não — treinamento presencial</div>
                              <div className="text-[10px] text-slate-500 leading-tight">
                                Aula já ministrada. Emitir certificado <b>na hora</b> com a rubrica de cada funcionário.
                              </div>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Date + Horário + (Digital: expires) */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {!isOrdemServico ? (
                        <div>
                          <label className="text-xs font-medium text-slate-700 block mb-1">
                            Data do treinamento
                          </label>
                          <Input
                            value={slot.trainingDate}
                            onChange={(e) =>
                              updateSlot(idx, { trainingDate: e.target.value })
                            }
                            placeholder="dd/mm/aaaa"
                            data-testid={`slot-${idx}-date`}
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="text-xs font-medium text-slate-700 block mb-1">
                            Data do documento
                          </label>
                          <Input
                            value="(data de admissão de cada funcionário)"
                            disabled
                            readOnly
                            title="A Ordem de Serviço sempre usa a data de admissão de cada funcionário"
                            className="bg-slate-100 text-slate-500 italic cursor-not-allowed"
                            data-testid={`slot-${idx}-date-disabled`}
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">
                          Horário
                        </label>
                        <select
                          value={slot.horario}
                          onChange={(e) =>
                            updateSlot(idx, { horario: e.target.value })
                          }
                          className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                          data-testid={`slot-${idx}-horario`}
                        >
                          <option value="">Usar padrão do curso</option>
                          <option value="08:00 ÁS 12:00 HS">
                            Manhã (08:00 às 12:00)
                          </option>
                          <option value="13:00 ÁS 17:00 HS">
                            Tarde (13:00 às 17:00)
                          </option>
                          <option value="08:00 ÁS 12:00 HS E DAS 13:00 ÁS 17:00 HS">
                            Dia inteiro (08–12 e 13–17)
                          </option>
                        </select>
                      </div>
                      {slot.mode === "digital" && (
                        <div>
                          <label className="text-xs font-medium text-slate-700 block mb-1">
                            Prazo p/ assinatura
                          </label>
                          <Input
                            type="datetime-local"
                            value={slot.expiresAt}
                            onChange={(e) =>
                              updateSlot(idx, { expiresAt: e.target.value })
                            }
                            data-testid={`slot-${idx}-expires`}
                          />
                        </div>
                      )}
                    </div>

                    {/* XLSX import */}
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        ref={(el) => {
                          fileRefs.current[idx] = el;
                        }}
                        type="file"
                        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        onChange={(e) =>
                          handleXlsxImportForSlot(idx, e.target.files?.[0])
                        }
                        data-testid={`slot-${idx}-file-input`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileRefs.current[idx]?.click()}
                        disabled={slot.importing || loading}
                        data-testid={`slot-${idx}-import-btn`}
                      >
                        {slot.importing ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                            Importando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-3.5 h-3.5 mr-2" />
                            Importar planilha
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        disabled={loading || slot.importing}
                        onClick={() => {
                          const usedElsewhere = usedIdsExcept(idx);
                          const eligible = employees.filter(
                            (e) =>
                              !usedElsewhere.has(e.id) &&
                              (slot.mode !== "manual" || e.has_signature)
                          );
                          const allSelected =
                            eligible.length > 0 &&
                            eligible.every((e) => slot.selectedIds[e.id]);
                          setSlots((prev) =>
                            prev.map((s, i) => {
                              if (i !== idx) return s;
                              if (allSelected) {
                                return { ...s, selectedIds: {}, importResult: null };
                              }
                              const nextSel = { ...s.selectedIds };
                              let nextImport = s.importResult || {
                                matched: [],
                                not_found: [],
                                stats: { rows: 0, no_signature: 0, not_found: 0 },
                                conflicts: 0,
                              };
                              const matched = [...nextImport.matched];
                              eligible.forEach((emp) => {
                                nextSel[emp.id] = true;
                                if (!matched.some((m) => m.id === emp.id)) {
                                  matched.push({
                                    id: emp.id,
                                    matricula: emp.matricula,
                                    nome: emp.nome,
                                    has_signature: !!emp.has_signature,
                                    posto_servico: emp.posto_servico || "",
                                    source: "todos",
                                  });
                                }
                              });
                              return {
                                ...s,
                                selectedIds: nextSel,
                                importResult: { ...nextImport, matched },
                              };
                            })
                          );
                          if (!allSelected) {
                            toast.success(
                              `${eligible.length} funcionário(s) selecionado(s)`
                            );
                          }
                        }}
                        data-testid={`slot-${idx}-select-all-btn`}
                      >
                        <Users className="w-3.5 h-3.5 mr-2" />
                        {(() => {
                          const usedElsewhere = usedIdsExcept(idx);
                          const eligible = employees.filter(
                            (e) =>
                              !usedElsewhere.has(e.id) &&
                              (slot.mode !== "manual" || e.has_signature)
                          );
                          const allSelected =
                            eligible.length > 0 &&
                            eligible.every((e) => slot.selectedIds[e.id]);
                          return allSelected
                            ? "Limpar seleção"
                            : `Todos os funcionários (${eligible.length})`;
                        })()}
                      </Button>
                      <span className="text-[11px] text-slate-500">
                        Colunas <strong>Nome</strong> e/ou{" "}
                        <strong>CPF</strong>. Selecionados automaticamente.
                      </span>
                    </div>

                    {/* Manual employee picker — collapsed by default */}
                    {(() => {
                      const isManualOpen = !!manualOpenBySlot[idx];
                      const rawQ = (manualQueryBySlot[idx] || "").trim();
                      const q = rawQ.toLowerCase();
                      const qDigits = rawQ.replace(/\D/g, "");
                      const usedElsewhere = usedIdsExcept(idx);
                      const matches = q.length >= 2
                        ? employees
                            .filter((e) => {
                              if (slot.selectedIds[e.id]) return true; // already selected — keep visible
                              if (usedElsewhere.has(e.id)) return false;
                              const nameHit = (e.nome || "").toLowerCase().includes(q);
                              const matHit = (e.matricula || "").toLowerCase().includes(q);
                              const cpfHaystack = (e.cpf_digits || (e.cpf_masked || "").replace(/\D/g, ""));
                              const cpfHit = qDigits.length >= 2 && cpfHaystack.includes(qDigits);
                              return nameHit || matHit || cpfHit;
                            })
                            .slice(0, 30)
                        : [];
                      return (
                        <div className="rounded-md border border-slate-200">
                          <button
                            type="button"
                            onClick={() =>
                              setManualOpenBySlot((p) => ({ ...p, [idx]: !p[idx] }))
                            }
                            className="w-full px-3 py-2 flex items-center justify-between text-xs text-slate-700 hover:bg-slate-50"
                            data-testid={`slot-${idx}-manual-toggle`}
                          >
                            <span className="flex items-center gap-2">
                              <Search className="w-3.5 h-3.5" />
                              Adicionar funcionário manualmente
                            </span>
                            <svg
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                                isManualOpen ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {isManualOpen && (
                            <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2">
                              <Input
                                value={manualQueryBySlot[idx] || ""}
                                onChange={(e) =>
                                  setManualQueryBySlot((p) => ({
                                    ...p, [idx]: e.target.value,
                                  }))
                                }
                                placeholder="Digite nome, matrícula ou CPF (mín. 2 caracteres)"
                                disabled={loading}
                                data-testid={`slot-${idx}-manual-search`}
                                className="h-9 text-sm"
                              />
                              {loading && (
                                <p className="text-[11px] text-slate-500">
                                  Carregando funcionários...
                                </p>
                              )}
                              {!loading && q.length < 2 && (
                                <p className="text-[11px] text-slate-500">
                                  Comece a digitar para buscar.
                                </p>
                              )}
                              {!loading && q.length >= 2 && matches.length === 0 && (
                                <p className="text-[11px] text-amber-700">
                                  Nenhum funcionário encontrado disponível
                                  (já alocado em outro slot ou inexistente).
                                </p>
                              )}
                              {matches.length > 0 && (
                                <div className="max-h-[24vh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
                                  {matches.map((emp) => {
                                    const isChecked = !!slot.selectedIds[emp.id];
                                    const noSig =
                                      slot.mode === "manual" &&
                                      !emp.has_signature;
                                    return (
                                      <label
                                        key={emp.id}
                                        className={`flex items-center gap-2 px-2 py-1.5 ${
                                          noSig
                                            ? "bg-slate-50/60 opacity-60 cursor-not-allowed"
                                            : "hover:bg-slate-50 cursor-pointer"
                                        }`}
                                        data-testid={`slot-${idx}-manual-result-${emp.matricula}`}
                                      >
                                        <Checkbox
                                          checked={isChecked}
                                          disabled={noSig}
                                          onCheckedChange={() => {
                                            if (noSig) return;
                                            // Add or remove from selectedIds AND, when adding,
                                            // also ensure they appear in importResult.matched so
                                            // the existing preview list + CIPA supervisor row work.
                                            setSlots((prev) =>
                                              prev.map((s, i) => {
                                                if (i !== idx) return s;
                                                const nextSel = { ...s.selectedIds };
                                                let nextImport = s.importResult;
                                                if (nextSel[emp.id]) {
                                                  delete nextSel[emp.id];
                                                } else {
                                                  nextSel[emp.id] = true;
                                                  // Append to matched if not there
                                                  if (!nextImport) {
                                                    nextImport = {
                                                      matched: [],
                                                      not_found: [],
                                                      stats: { rows: 0, no_signature: 0, not_found: 0 },
                                                      conflicts: 0,
                                                    };
                                                  }
                                                  if (!nextImport.matched.some((m) => m.id === emp.id)) {
                                                    nextImport = {
                                                      ...nextImport,
                                                      matched: [
                                                        ...nextImport.matched,
                                                        {
                                                          id: emp.id,
                                                          matricula: emp.matricula,
                                                          nome: emp.nome,
                                                          has_signature: !!emp.has_signature,
                                                          posto_servico: emp.posto_servico || "",
                                                          source: "manual",
                                                        },
                                                      ],
                                                    };
                                                  }
                                                }
                                                return { ...s, selectedIds: nextSel, importResult: nextImport };
                                              }),
                                            );
                                          }}
                                        />
                                        <span className="font-mono text-[11px] text-blue-600 w-12 flex-shrink-0">
                                          {emp.matricula}
                                        </span>
                                        <span className="flex-1 text-xs text-slate-900 truncate">
                                          {emp.nome}
                                        </span>
                                        {noSig && (
                                          <span className="text-[10px] text-amber-700 whitespace-nowrap">
                                            sem rúbrica
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {matches.length === 30 && (
                                <p className="text-[10px] text-slate-500">
                                  Mostrando 30 primeiros — refine a busca para ver mais.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Import result */}
                    {slot.importResult && (
                      <div
                        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs space-y-1.5"
                        data-testid={`slot-${idx}-result`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-blue-900 font-medium">
                            Resultado — {slot.importResult.stats.rows} linha(s)
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-slate-700">
                          <div>
                            <strong className="text-emerald-700">
                              {selectedCount}
                            </strong>{" "}
                            selecionados
                          </div>
                          <div>
                            <strong className="text-amber-700">
                              {slot.importResult.stats.no_signature}
                            </strong>{" "}
                            sem rúbrica
                          </div>
                          <div>
                            <strong className="text-rose-700">
                              {slot.importResult.stats.not_found}
                            </strong>{" "}
                            não localizados
                          </div>
                          <div>
                            <strong className="text-orange-700">
                              {slot.importResult.conflicts || 0}
                            </strong>{" "}
                            em outro slot
                          </div>
                        </div>
                        {noSig.length > 0 && (
                          <details className="text-amber-800">
                            <summary className="cursor-pointer flex items-center gap-2 flex-wrap">
                              <span>Ver {noSig.length} sem rúbrica</span>
                              <button
                                type="button"
                                data-testid={`slot-${idx}-export-no-sig`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const rows = noSig.map((m) => ({
                                    Matrícula: m.matricula || "",
                                    Nome: m.nome || "",
                                    "Posto de Serviço":
                                      m.posto_servico || "",
                                  }));
                                  if (!rows.length) return;
                                  const ws = XLSX.utils.json_to_sheet(rows);
                                  ws["!cols"] = [
                                    { wch: 12 },
                                    { wch: 40 },
                                    { wch: 32 },
                                  ];
                                  const wb = XLSX.utils.book_new();
                                  XLSX.utils.book_append_sheet(
                                    wb,
                                    ws,
                                    "Sem rubrica"
                                  );
                                  XLSX.writeFile(
                                    wb,
                                    `slot${idx + 1}_sem_rubrica.xlsx`
                                  );
                                  toast.success(`Exportados ${rows.length}`);
                                }}
                                className="ml-1 px-2 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-semibold uppercase tracking-wide"
                              >
                                Exportar XLSX
                              </button>
                            </summary>
                            <ul className="mt-1 ml-4 list-disc">
                              {noSig.map((m) => (
                                <li
                                  key={m.id}
                                  className="font-mono text-[10px]"
                                >
                                  {m.matricula} — {m.nome}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                        {slot.importResult.not_found.length > 0 && (
                          <details className="text-rose-800">
                            <summary className="cursor-pointer">
                              Ver {slot.importResult.not_found.length} não
                              localizados
                            </summary>
                            <ul className="mt-1 ml-4 list-disc">
                              {slot.importResult.not_found.map((n, i) => (
                                <li key={i} className="text-[11px]">
                                  {n.nome_input || "(sem nome)"}{" "}
                                  {n.cpf_input ? `— CPF ${n.cpf_input}` : ""}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}

                    {/* Matched employees preview list (only matched, not full directory) */}
                    {slot.importResult &&
                      slot.importResult.matched.length > 0 && (
                        <div className="rounded-md border border-slate-200">
                          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-600 flex items-center justify-between">
                            <span>Funcionários do slot {idx + 1}</span>
                            <span>{selectedCount} selecionado(s)</span>
                          </div>
                          <div className="max-h-[28vh] overflow-y-auto divide-y divide-slate-100">
                            {slot.importResult.matched.map((m) => {
                              const disabled =
                                slot.mode === "manual" && !m.has_signature;
                              const isChecked = !!slot.selectedIds[m.id];
                              const emp = empById[m.id];
                              const supervisorValue =
                                supervisorEdits[m.id] !== undefined
                                  ? supervisorEdits[m.id]
                                  : emp?.supervisor || "";
                              return (
                                <div
                                  key={m.id}
                                  className={`px-3 py-2 ${
                                    disabled
                                      ? "bg-slate-50/40 opacity-60"
                                      : "hover:bg-slate-50"
                                  }`}
                                  data-testid={`slot-${idx}-emp-${m.matricula}`}
                                >
                                  <label
                                    className={`flex items-center gap-3 ${
                                      disabled
                                        ? "cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={() =>
                                        !disabled && toggleSlotMatch(idx, m.id)
                                      }
                                      disabled={disabled}
                                    />
                                    <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center">
                                      <span className="col-span-2 font-mono text-xs text-blue-600">
                                        {m.matricula}
                                      </span>
                                      <span className="col-span-7 text-sm text-slate-900 truncate">
                                        {m.nome}
                                      </span>
                                      <span className="col-span-3 text-right">
                                        {slot.mode !== "manual" ? (
                                          <span className="inline-flex items-center text-sky-700 text-xs">
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                            Assina via OTP
                                          </span>
                                        ) : m.has_signature ? (
                                          <span className="inline-flex items-center text-emerald-700 text-xs">
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                            Rúbrica
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center text-amber-700 text-xs">
                                            <AlertCircle className="w-3 h-3 mr-1" />
                                            Sem rúbrica
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </label>
                                  {isCipa && isChecked && emp && (
                                    <div
                                      className="mt-2 ml-7 flex items-center gap-2"
                                      data-testid={`slot-${idx}-supervisor-row-${m.matricula}`}
                                    >
                                      <label className="text-[11px] uppercase tracking-wide text-slate-500 whitespace-nowrap">
                                        Supervisor:
                                      </label>
                                      <SupervisorAutocompleteInput
                                        value={supervisorValue}
                                        onChange={(v) =>
                                          setSupervisorEdits((prev) => ({
                                            ...prev,
                                            [m.id]: v,
                                          }))
                                        }
                                        onSelect={(cand) => {
                                          setSupervisorEdits((prev) => ({
                                            ...prev,
                                            [m.id]: cand.nome,
                                          }));
                                          saveSupervisor(emp, cand.nome);
                                        }}
                                        onCommit={() => saveSupervisor(emp)}
                                        placeholder="Digite para buscar entre os funcionários…"
                                        disabled={savingSupervisorId === m.id}
                                        saving={savingSupervisorId === m.id}
                                        options={employees.filter((cand) => {
                                          if (cand.id === emp.id) return false;
                                          const q = supervisorValue
                                            .trim()
                                            .toLowerCase();
                                          if (!q) return false;
                                          return (
                                            (cand.nome || "")
                                              .toLowerCase()
                                              .includes(q) ||
                                            (cand.matricula || "")
                                              .toLowerCase()
                                              .includes(q)
                                          );
                                        })}
                                        testIdInput={`slot-${idx}-supervisor-input-${m.matricula}`}
                                        testIdDropdown={`slot-${idx}-supervisor-dropdown-${m.matricula}`}
                                        optionTestIdPrefix={`slot-${idx}-supervisor-option-`}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-slate-100 pt-3">
          <div className="flex-1 text-xs text-slate-600">
            {slotsSavedCount > 0 && (
              <span className="text-emerald-700 font-medium">
                {slotsSavedCount} já gerada(s) •{" "}
              </span>
            )}
            <span data-testid="generate-list-ready-count">
              {slotsReady.length} pronta(s) para emitir
            </span>
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generatingAll}
            data-testid="generate-list-close-btn"
          >
            {slotsSavedCount > 0 ? "Fechar" : "Cancelar"}
          </Button>
          <Button
            onClick={handleGenerateAll}
            disabled={generatingAll || slotsReady.length === 0}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="generate-list-confirm-btn"
          >
            {generatingAll ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando ({slotsReady.length})...
              </>
            ) : (
              <>
                <ListPlus className="w-4 h-4 mr-2" />
                Gerar {slotsReady.length} lista(s)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------- Filtered Download Dialog -------------------- */
const FilterDownloadDialog = ({ open, onOpenChange, list, course }) => {
  const participants = list?.participants || [];
  const [checked, setChecked] = useState({});
  const [posto, setPosto] = useState("");
  const [downloading, setDownloading] = useState(null);

  const postos = useMemo(() => {
    const set = new Set();
    participants.forEach((p) => {
      const v = (p.posto_servico || "").trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort();
  }, [participants]);

  useEffect(() => {
    if (open) {
      setChecked({});
      setPosto("");
    }
  }, [open, list?.id]);

  const selectedIds = Object.entries(checked)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const applyPosto = (value) => {
    setPosto(value);
    if (!value) return;
    const next = {};
    participants.forEach((p) => {
      if ((p.posto_servico || "").trim() === value) next[p.employee_id] = true;
    });
    setChecked(next);
  };

  const toggleOne = (id) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    if (selectedIds.length === participants.length) setChecked({});
    else {
      const next = {};
      participants.forEach((p) => (next[p.employee_id] = true));
      setChecked(next);
    }
  };

  const handleDownload = async (kind) => {
    if (selectedIds.length === 0) {
      toast.error("Selecione ao menos um funcionário");
      return;
    }
    setDownloading(kind);
    const isOS =
      course?.course_key === "ordem_servico" ||
      course?.course_key === "declaracao_act";
    try {
      const endpoint = kind === "certs" ? "certificates" : "pdf";
      const res = await axios.get(
        `${API}/admin/courses/lists/${list.id}/${endpoint}`,
        {
          params: { filter_ids: selectedIds.join(",") },
          responseType: "blob",
          timeout: 180000,
        }
      );
      const isZip = kind === "certs" || isOS;
      const blob = new Blob([res.data], {
        type: isZip ? "application/zip" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = posto
        ? posto.toLowerCase().replace(/\s+/g, "_")
        : `${selectedIds.length}_selecionados`;
      a.download =
        kind === "certs"
          ? `certificados_${course.course_key}_${suffix}.zip`
          : isOS
          ? `${course.course_key}_${suffix}.zip`
          : `lista_presenca_${course.course_key}_${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download concluído");
    } catch (e) {
      let detail = "Erro ao gerar arquivo";
      try {
        const text = await e?.response?.data?.text?.();
        if (text) detail = JSON.parse(text).detail || detail;
      } catch (_) {
        /* ignore */
      }
      toast.error(detail);
    } finally {
      setDownloading(null);
    }
  };

  const showCerts =
    !!course?.has_certificate && course?.course_key !== "cipa";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-700" />
            Download filtrado
          </DialogTitle>
          <DialogDescription>
            Escolha um posto ou marque funcionários específicos para baixar
            apenas a parte desejada da lista de {list?.training_date}.
          </DialogDescription>
        </DialogHeader>

        {postos.length > 0 && (
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Filtrar por posto
            </label>
            <select
              value={posto}
              onChange={(e) => applyPosto(e.target.value)}
              className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
              data-testid="filter-posto-select"
            >
              <option value="">— Todos os postos —</option>
              {postos.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer px-1">
          <input
            type="checkbox"
            checked={
              selectedIds.length === participants.length &&
              participants.length > 0
            }
            onChange={toggleAll}
            className="rounded"
            data-testid="filter-select-all"
          />
          <span>
            {selectedIds.length > 0
              ? `${selectedIds.length} de ${participants.length} selecionado(s)`
              : "Selecionar todos"}
          </span>
        </label>

        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100 min-h-[120px] max-h-[40vh]">
          {participants.map((p) => (
            <label
              key={p.employee_id}
              className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
              data-testid={`filter-participant-${p.employee_id}`}
            >
              <input
                type="checkbox"
                checked={!!checked[p.employee_id]}
                onChange={() => toggleOne(p.employee_id)}
                className="rounded"
              />
              <span className="flex-1 min-w-0">
                <span className="block truncate font-medium text-slate-800">
                  {p.nome}
                </span>
                <span className="block text-xs text-slate-500 truncate">
                  {p.matricula}
                  {p.posto_servico ? ` • ${p.posto_servico}` : ""}
                </span>
              </span>
            </label>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => handleDownload("pdf")}
            disabled={downloading !== null || selectedIds.length === 0}
            data-testid="filter-download-pdf-btn"
          >
            {downloading === "pdf" ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Baixar lista de presença
          </Button>
          {showCerts && (
            <Button
              onClick={() => handleDownload("certs")}
              disabled={downloading !== null || selectedIds.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="filter-download-certs-btn"
            >
              {downloading === "certs" ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Award className="w-4 h-4 mr-1.5" />
              )}
              Baixar certificados
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------- History Dialog -------------------- */
const HistoryDialog = ({ open, onOpenChange, course, refreshCounters, onRequestCipaEmit }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingCertId, setDownloadingCertId] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, list: null });
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailPosto, setEmailPosto] = useState("");
  const [emailGestao, setEmailGestao] = useState("");
  const [emailSupervisorId, setEmailSupervisorId] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, list: null });
  const [deleting, setDeleting] = useState(false);
  const [filterDialog, setFilterDialog] = useState({ open: false, list: null });
  const [qReport, setQReport] = useState({ open: false, list: null });
  const [downloadingPendingId, setDownloadingPendingId] = useState(null);
  const [pausingId, setPausingId] = useState(null);
  const [receiptsList, setReceiptsList] = useState(null);
  const [participantsList, setParticipantsList] = useState(null);

  const handleTogglePause = async (list) => {
    setPausingId(list.id);
    try {
      const r = await axios.post(
        `${API}/admin/courses/lists/${list.id}/pause-signatures`,
        { paused: !list.signatures_paused },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      setLists((prev) =>
        prev.map((l) =>
          l.id === list.id ? { ...l, signatures_paused: r.data.paused } : l
        )
      );
      toast.success(
        r.data.paused
          ? "Assinaturas pausadas — a convocação sumiu do portal dos funcionários"
          : "Assinaturas retomadas — a convocação voltou ao portal dos funcionários"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao pausar/retomar");
    } finally {
      setPausingId(null);
    }
  };

  const handleDownloadPendingExcel = async (list) => {
    setDownloadingPendingId(list.id);
    try {
      const res = await axios.get(
        `${API}/admin/courses/lists/${list.id}/pending-excel`,
        {
          responseType: "blob",
          timeout: 30000,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pendentes_${course.course_key}_${list.id.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel de pendentes baixado");
    } catch (e) {
      let detail = "Erro ao gerar Excel";
      try {
        const text = await e?.response?.data?.text?.();
        if (text) detail = JSON.parse(text).detail || detail;
      } catch (_) {
        /* ignore */
      }
      toast.error(detail);
    } finally {
      setDownloadingPendingId(null);
    }
  };
  // Bulk selection for the "Send selected by e-mail" action.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);  // {current, total}
  // Bulk DOWNLOAD state — separate from the email bulk action.
  const [bulkDlDialog, setBulkDlDialog] = useState(false);
  const [bulkDlPosto, setBulkDlPosto] = useState("");
  const [bulkDlGestao, setBulkDlGestao] = useState("");
  const [bulkDlSupervisorId, setBulkDlSupervisorId] = useState("");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDlStatus, setBulkDlStatus] = useState("");  // user-facing status text
  // Supervisor list for the email-modal dropdown (CIPA only).
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  useEffect(() => {
    const shouldFetch = (emailDialog.open || bulkDlDialog) && course?.course_key === "cipa";
    if (!shouldFetch) return;
    if (supervisorOptions.length) return;
    axios
      .get(`${API}/admin/employees-for-list`)
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
        const withSig = items
          .filter((e) => e.has_signature)
          .map((e) => ({ id: e.id, nome: e.nome, matricula: e.matricula }));
        setSupervisorOptions(withSig);
      })
      .catch(() => setSupervisorOptions([]));
  }, [emailDialog.open, bulkDlDialog, course?.course_key, supervisorOptions.length]);

  const fetchLists = useCallback(async () => {
    if (!course) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/courses/${course.course_key}/lists`,
        { timeout: 15000 }
      );
      setLists(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  }, [course]);

  useEffect(() => {
    if (open) fetchLists();
  }, [open, fetchLists]);

  const handleDownload = async (list) => {
    setDownloadingId(list.id);
    const isOrdemServico =
      course?.course_key === "ordem_servico" ||
      course?.course_key === "declaracao_act";
    // Large lists (any course) can exceed the proxy timeout in
    // production — use the background-job flow above 100 participants.
    const useJob = isOrdemServico || (list.count || 0) > 100;
    try {
      if (useJob) {
        // OS lists may contain hundreds of employees → use the
        // background-job flow (avoids the 60s K8s ingress timeout).
        const startRes = await axios.post(
          `${API}/admin/courses/lists/${list.id}/pdf-async`
        );
        const jobId = startRes.data.job_id;
        toast.info("Gerando arquivo em segundo plano… isso pode demorar.");
        // Poll every 4s until status === "done" or failed (max 15 min).
        let attempts = 0;
        const maxAttempts = 225;
        while (attempts < maxAttempts) {
          await new Promise((r) => setTimeout(r, 4000));
          attempts += 1;
          const st = await axios.get(`${API}/admin/courses/jobs/${jobId}`);
          if (st.data.status === "done") break;
          if (st.data.status === "failed") {
            throw new Error(st.data.error || "Falha na geração");
          }
        }
        if (attempts >= maxAttempts) {
          throw new Error("Tempo limite excedido (15 min)");
        }
        // Download the artifact now that it's ready.
        const dl = await axios.get(
          `${API}/admin/courses/jobs/${jobId}/download`,
          { responseType: "blob", timeout: 120000 }
        );
        const blob = new Blob([dl.data], {
          type: isOrdemServico ? "application/zip" : "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = isOrdemServico
          ? `${course.course_key}_${list.id.slice(0, 8)}.zip`
          : `lista_presenca_${course.course_key}_${list.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Arquivo baixado!");
        return;
      }
      const res = await axios.get(
        `${API}/admin/courses/lists/${list.id}/pdf`,
        { responseType: "blob", timeout: 120000 }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lista_presenca_${course.course_key}_${list.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          (isOrdemServico
            ? "Erro ao gerar Ordem de Serviço"
            : "Erro ao gerar PDF")
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadCertificates = async (list) => {
    if (course?.course_key === "cipa" && typeof onRequestCipaEmit === "function") {
      // Hand off the CIPA emission flow to the parent (it shows the
      // posto/gestão prompt, then triggers the download).
      onRequestCipaEmit(list);
      return;
    }
    setDownloadingCertId(list.id);
    try {
      const { certFormat } = await runCertJobAndDownload(
        list.id, course.course_key,
      );
      if (certFormat === "docx") {
        toast.warning(
          "⚠️ Certificados saíram como .DOCX (conversor PDF indisponível no servidor). Contate o suporte.",
          { duration: 12000 }
        );
      } else {
        toast.success("Certificados gerados");
      }
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Erro ao gerar certificados";
      toast.error(detail);
    } finally {
      setDownloadingCertId(null);
    }
  };

  const toggleSelectOne = (listId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId);
      else next.add(listId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === lists.length) return new Set();
      return new Set(lists.map((l) => l.id));
    });
  };

  const handleBulkEmail = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!emailTo.trim()) {
      toast.error("Informe o destinatário");
      return;
    }
    if (course?.course_key === "cipa" && !emailGestao.trim()) {
      toast.error("Informe a Gestão para CIPA");
      return;
    }
    setBulkSending(true);
    setBulkProgress({ current: 0, total: ids.length });
    const errors = [];
    let ok = 0;
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      setBulkProgress({ current: i + 1, total: ids.length });
      const payload = {
        to: emailTo.trim(),
        cc: emailCc.trim() || undefined,
      };
      if (course?.course_key === "cipa") {
        if (emailPosto.trim()) payload.posto = emailPosto.trim();
        if (emailSupervisorId) payload.supervisor_id = emailSupervisorId;
        payload.gestao = emailGestao.trim();
      }
      try {
        await axios.post(
          `${API}/admin/courses/lists/${id}/email`,
          payload,
          { timeout: 240000 }
        );
        ok += 1;
      } catch (e) {
        const target = lists.find((l) => l.id === id);
        const dt = target?.training_date || id.slice(0, 8);
        errors.push(`${dt}: ${e?.response?.data?.detail || e?.message || "erro"}`);
      }
    }
    setBulkSending(false);
    setBulkProgress(null);
    setBulkDialog(false);
    setSelectedIds(new Set());
    if (errors.length === 0) {
      toast.success(`✅ ${ok} email(s) enviado(s) com sucesso.`);
    } else {
      toast.error(
        `${ok} OK, ${errors.length} falha(s):\n` + errors.slice(0, 5).join("\n")
      );
    }
  };

  // Bulk download: POST list_ids to the async job endpoint, poll until
  // done, then trigger 2 sequential downloads (listas + certificados).
  const handleBulkDownload = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (course?.course_key === "cipa" && !bulkDlGestao.trim()) {
      toast.error("Informe a Gestão para CIPA");
      return;
    }
    setBulkDownloading(true);
    setBulkDlStatus(`Preparando ${ids.length} lista(s)...`);
    try {
      const body = { list_ids: ids };
      if (course?.course_key === "cipa") {
        body.gestao = bulkDlGestao.trim();
        if (bulkDlPosto.trim()) body.posto = bulkDlPosto.trim();
        if (bulkDlSupervisorId) body.supervisor_id = bulkDlSupervisorId;
      }
      const start = await axios.post(
        `${API}/admin/courses/${course.course_key}/lists/bulk-download-async`,
        body,
        { timeout: 30000 }
      );
      const jobId = start.data.job_id;
      // Poll every 4s, max 30 min
      const maxAttempts = 450;
      let attempts = 0;
      let jobResult = null;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 4000));
        attempts += 1;
        const st = await axios.get(`${API}/admin/courses/jobs/${jobId}`);
        if (st.data.status === "done") {
          jobResult = st.data;
          break;
        }
        if (st.data.status === "failed") {
          throw new Error(st.data.error || "Falha no processamento");
        }
        const prog = st.data.progress;
        if (prog) {
          setBulkDlStatus(
            `Processando ${prog.current + 1}/${prog.total} listas...`
          );
        }
      }
      if (!jobResult) throw new Error("Tempo limite excedido");

      // Trigger 2 sequential downloads
      const triggerDownload = async (kind, defaultName) => {
        const dl = await axios.get(
          `${API}/admin/courses/jobs/${jobId}/download-bulk/${kind}`,
          { responseType: "blob", timeout: 180000 }
        );
        const blob = new Blob([dl.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          jobResult[`${kind}_filename`] || defaultName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      };
      setBulkDlStatus("Baixando lista de presença...");
      await triggerDownload("listas", `listas_presenca_${course.course_key}.zip`);
      await new Promise((r) => setTimeout(r, 600));  // brief pause between downloads
      setBulkDlStatus("Baixando certificados...");
      await triggerDownload("certs", `certificados_${course.course_key}.zip`);

      const errs = jobResult.errors || [];
      const fellBackToDocx = jobResult.cert_format === "docx";
      if (fellBackToDocx) {
        toast.warning(
          "⚠️ Certificados saíram como .DOCX (conversor PDF indisponível no servidor). Contate o suporte.",
          { duration: 12000 }
        );
      }
      if (errs.length === 0) {
        toast.success(`✅ ${jobResult.ok_count} lista(s) — 2 arquivos baixados.`);
      } else {
        toast.success(
          `${jobResult.ok_count} OK, ${errs.length} com erro. Arquivos baixados.`
        );
      }
      setBulkDlDialog(false);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e?.message || "Erro no download em lote"
      );
    } finally {
      setBulkDownloading(false);
      setBulkDlStatus("");
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.list) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/courses/lists/${deleteConfirm.list.id}`);
      toast.success("Lista removida");
      setDeleteConfirm({ open: false, list: null });
      fetchLists();
      refreshCounters?.();
    } catch (e) {
      toast.error("Erro ao remover lista");
    } finally {
      setDeleting(false);
    }
  };

  // Edit list date — only training_date / horario, preserves the rest.
  const [editDateDialog, setEditDateDialog] = useState({ open: false, list: null });
  const [editDateValue, setEditDateValue] = useState("");
  const [editHorarioValue, setEditHorarioValue] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const openEditDate = (list) => {
    setEditDateValue(list?.training_date || "");
    setEditHorarioValue(list?.horario || "");
    setEditDateDialog({ open: true, list });
  };

  const handleSaveDate = async () => {
    if (!editDateDialog.list) return;
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(editDateValue.trim())) {
      toast.error("Use o formato dd/mm/aaaa");
      return;
    }
    setSavingDate(true);
    try {
      await axios.patch(
        `${API}/admin/courses/lists/${editDateDialog.list.id}/date`,
        { training_date: editDateValue.trim(), horario: editDateHorarioOrEmpty() },
      );
      toast.success("Data atualizada — novas emissões usarão a data nova");
      setEditDateDialog({ open: false, list: null });
      fetchLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar data");
    } finally {
      setSavingDate(false);
    }
  };

  const editDateHorarioOrEmpty = () => editHorarioValue || "";

  const handleSendEmail = async () => {
    if (!emailDialog.list) return;
    if (!emailTo.trim()) {
      toast.error("Informe ao menos um destinatário");
      return;
    }
    setSendingEmail(true);
    try {
      const payload = { to: emailTo.trim(), cc: emailCc.trim() };
      if (course?.course_key === "cipa") {
        if (!emailGestao.trim()) {
          toast.error("Informe a Gestão para CIPA");
          setSendingEmail(false);
          return;
        }
        // Posto is optional — each participant uses their own
        // `posto_servico`. The field acts as a fallback only.
        if (emailPosto.trim()) payload.posto = emailPosto.trim();
        if (emailSupervisorId) payload.supervisor_id = emailSupervisorId;
        payload.gestao = emailGestao.trim();
      }
      const res = await axios.post(
        `${API}/admin/courses/lists/${emailDialog.list.id}/email`,
        payload,
        { timeout: 240000 }
      );
      toast.success(
        res?.data?.background
          ? res.data.message || "Email será enviado em segundo plano"
          : "Email enviado com sucesso"
      );
      setEmailDialog({ open: false, list: null });
      setEmailTo("");
      setEmailCc("");
      setEmailPosto("");
      setEmailGestao("");
      setEmailSupervisorId("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erro ao enviar email";
      toast.error(detail);
    } finally {
      setSendingEmail(false);
    }
  };

  const courseHasCertificate = !!course?.has_certificate;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-700" />
              Histórico — {course?.titulo}
            </DialogTitle>
            <DialogDescription>
              Listas geradas anteriormente para este curso. Clique em PDF para
              baixar.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : lists.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              Nenhuma lista gerada ainda. Use "Gerar Lista" para criar.
            </div>
          ) : (
            <>
              {/* Bulk action bar — shown when at least 1 list is selected */}
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border border-slate-200 rounded-md mb-2">
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === lists.length && lists.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded"
                    data-testid="history-select-all"
                  />
                  <span>
                    {selectedIds.size > 0
                      ? `${selectedIds.size} selecionada(s)`
                      : "Selecionar todas"}
                  </span>
                </label>
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2">
                    {courseHasCertificate && (
                      <Button
                        size="sm"
                        onClick={() => {
                          if (course?.course_key === "cipa") {
                            setBulkDlPosto("");
                            setBulkDlGestao("");
                            setBulkDlSupervisorId("");
                            setBulkDlDialog(true);
                          } else {
                            handleBulkDownload();
                          }
                        }}
                        disabled={bulkDownloading}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="history-bulk-download-btn"
                        title="Baixar 2 ZIPs: listas de presença + certificados"
                      >
                        {bulkDownloading ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Baixar selecionadas
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => setBulkDialog(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="history-bulk-email-btn"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Enviar selecionadas por e-mail
                    </Button>
                  </div>
                )}
              </div>
            <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
              {lists.map((list) => (
                <div
                  key={list.id}
                  className="px-3 py-3 hover:bg-slate-50"
                  data-testid={`history-list-${list.id}`}
                >
                  <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(list.id)}
                    onChange={() => toggleSelectOne(list.id)}
                    className="rounded mt-2.5"
                    data-testid={`history-select-${list.id}`}
                  />
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      Treinamento em {list.training_date}
                    </p>
                    <p className="text-xs text-slate-500">
                      <Users className="w-3 h-3 inline mr-1" />
                      {list.count} participante(s) •{" "}
                      Criada em{" "}
                      {new Date(list.created_at).toLocaleString("pt-BR")}
                    </p>
                    {list.mode === "digital" && list.signed_count != null && (
                      <p
                        className="text-xs mt-0.5"
                        data-testid={`history-sign-status-${list.id}`}
                      >
                        {list.respondidos_count != null && (
                          <span className="text-violet-700 font-semibold">
                            {list.respondidos_count} responderam o questionário •{" "}
                          </span>
                        )}
                        <span className="text-emerald-700 font-semibold">
                          {list.signed_count} assinaram
                          {list.respondidos_count != null ? " a lista" : ""}
                        </span>
                        {list.pending_count > 0 && (
                          <span className="text-amber-700 font-semibold">
                            {" "}• {list.pending_count} pendente(s)
                            {list.respondidos_count != null ? " de assinatura" : ""}
                          </span>
                        )}
                        {list.signatures_paused && (
                          <span
                            className="text-orange-600 font-semibold"
                            data-testid={`history-paused-badge-${list.id}`}
                          >
                            {" "}• ⏸ assinaturas pausadas
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  </div>
                  <div className="mt-2 ml-8 flex flex-wrap items-center gap-1.5">
                  {list.mode === "digital" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setParticipantsList(list)}
                      data-testid={`history-participants-${list.id}`}
                      title="Adicionar/remover participantes"
                      className="h-7 px-2 text-[11px] text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                    >
                      <Users className="w-3.5 h-3.5 mr-1" />Pessoas
                    </Button>
                  )}
                  {list.mode === "digital" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setReceiptsList(list)}
                      data-testid={`history-receipts-${list.id}`}
                      title="Comprovantes individuais de assinatura"
                      className="h-7 px-2 text-[11px] text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                    >
                      <FileCheck2 className="w-3.5 h-3.5 mr-1" />Comprovantes
                    </Button>
                  )}
                  {list.mode === "digital" && list.status !== "closed" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTogglePause(list)}
                      disabled={pausingId === list.id}
                      data-testid={`history-pause-${list.id}`}
                      title={
                        list.signatures_paused
                          ? "Retomar assinaturas (volta a aparecer no portal)"
                          : "Pausar assinaturas (some do portal dos funcionários)"
                      }
                      className={
                        "h-7 px-2 text-[11px] " + (list.signatures_paused
                          ? "text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          : "text-orange-600 hover:text-orange-700 hover:bg-orange-50")
                      }
                    >
                      {pausingId === list.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : list.signatures_paused ? (
                        <><Play className="w-3.5 h-3.5 mr-1" />Retomar</>
                      ) : (
                        <><Pause className="w-3.5 h-3.5 mr-1" />Pausar</>
                      )}
                    </Button>
                  )}
                  {list.mode === "digital" && list.pending_count > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPendingExcel(list)}
                      disabled={downloadingPendingId === list.id}
                      data-testid={`history-pending-excel-${list.id}`}
                      title={`Baixar Excel com ${list.pending_count} pendente(s) de assinatura`}
                      className="h-7 px-2 text-[11px] text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                    >
                      {downloadingPendingId === list.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Pendentes</>
                      )}
                    </Button>
                  )}
                  {course?.course_key === "questionario_psicossocial" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setQReport({ open: true, list })}
                      data-testid={`history-qreport-${list.id}`}
                      title="Relatório de gráficos (pizza) das respostas"
                      className="h-7 px-2 text-[11px] text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50"
                    >
                      <PieChartIcon className="w-3.5 h-3.5 mr-1" />Gráficos
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilterDialog({ open: true, list })}
                    data-testid={`history-filter-${list.id}`}
                    title="Download filtrado (por posto ou funcionário)"
                    className="h-7 px-2 text-[11px] text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                  >
                    <Filter className="w-3.5 h-3.5 mr-1" />Filtrar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(list)}
                    disabled={downloadingId === list.id}
                    data-testid={`history-pdf-${list.id}`}
                    title="Baixar lista de presença (PDF)"
                    className="h-7 px-2 text-[11px]"
                  >
                    {downloadingId === list.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><Download className="w-3.5 h-3.5 mr-1" />PDF</>
                    )}
                  </Button>
                  {courseHasCertificate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadCertificates(list)}
                      disabled={downloadingCertId === list.id}
                      data-testid={`history-certs-${list.id}`}
                      title="Emitir certificados (.zip)"
                      className="h-7 px-2 text-[11px] text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                    >
                      {downloadingCertId === list.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><Award className="w-3.5 h-3.5 mr-1" />Certificados</>
                      )}
                    </Button>
                  )}
                  {(courseHasCertificate || course?.course_key === "ordem_servico") && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEmailDialog({ open: true, list });
                        setEmailTo("");
                        setEmailCc("");
                        setEmailPosto("");
                        setEmailGestao("");
                        setEmailSupervisorId("");
                      }}
                      data-testid={`history-email-${list.id}`}
                      title="Enviar lista + certificados por email"
                      className="h-7 px-2 text-[11px] text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" />E-mail
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDate(list)}
                    className="h-7 px-2 text-[11px] text-amber-600 hover:bg-amber-50"
                    data-testid={`history-edit-date-${list.id}`}
                    title="Editar data do treinamento"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1" />Data
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirm({ open: true, list })}
                    className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50"
                    data-testid={`history-delete-${list.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />Excluir
                  </Button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuestionarioReportDialog
        open={qReport.open}
        list={qReport.list}
        onClose={() => setQReport({ open: false, list: null })}
      />
      <SignatureReceiptsDialog
        open={!!receiptsList}
        onOpenChange={(o) => !o && setReceiptsList(null)}
        list={receiptsList}
      />
      <ListParticipantsDialog
        open={!!participantsList}
        onOpenChange={(o) => !o && setParticipantsList(null)}
        list={participantsList}
        onChanged={() => { fetchLists(); }}
      />
      <FilterDownloadDialog
        open={filterDialog.open}
        onOpenChange={(o) =>
          setFilterDialog({ open: o, list: o ? filterDialog.list : null })
        }
        list={filterDialog.list}
        course={course}
      />

      <Dialog
        open={deleteConfirm.open}
        onOpenChange={(o) =>
          setDeleteConfirm({ open: o, list: o ? deleteConfirm.list : null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir lista</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. A lista de{" "}
              <strong>{deleteConfirm.list?.training_date}</strong> com{" "}
              <strong>{deleteConfirm.list?.count}</strong> participante(s) será
              removida do histórico.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ open: false, list: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-list-btn"
            >
              {deleting ? "Removendo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit list date dialog */}
      <Dialog
        open={editDateDialog.open}
        onOpenChange={(o) =>
          setEditDateDialog({ open: o, list: o ? editDateDialog.list : null })
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Calendar className="w-5 h-5" />
              Editar data do treinamento
            </DialogTitle>
            <DialogDescription>
              Atualiza a data desta lista. Próximas emissões (PDF da lista e
              certificados) usarão a data nova. PDFs já baixados não mudam.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Data do treinamento <span className="text-rose-600">*</span>
              </label>
              <Input
                value={editDateValue}
                onChange={(e) => setEditDateValue(e.target.value)}
                placeholder="dd/mm/aaaa"
                disabled={savingDate}
                data-testid="edit-date-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Formato dd/mm/aaaa (ex.: 10/04/2026)
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Horário (opcional)
              </label>
              <select
                value={editHorarioValue}
                onChange={(e) => setEditHorarioValue(e.target.value)}
                disabled={savingDate}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="edit-date-horario"
              >
                <option value="">Manter atual</option>
                <option value="08:00 ÁS 12:00 HS">Manhã (08:00 às 12:00)</option>
                <option value="13:00 ÁS 17:00 HS">Tarde (13:00 às 17:00)</option>
                <option value="08:00 ÁS 12:00 HS E DAS 13:00 ÁS 17:00 HS">
                  Dia inteiro (08–12 e 13–17)
                </option>
              </select>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              ℹ️ Para regenerar os PDFs com a data nova, baixe novamente
              pela aba "Baixar certificados" ou "Baixar lista".
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDateDialog({ open: false, list: null })}
              disabled={savingDate}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveDate}
              disabled={savingDate || !editDateValue.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="edit-date-save-btn"
            >
              {savingDate ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Salvar data
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk DOWNLOAD modal (CIPA only — collects gestão + posto + supervisor) */}
      <Dialog
        open={bulkDlDialog}
        onOpenChange={(o) => !bulkDownloading && setBulkDlDialog(o)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-amber-600" />
              Baixar {selectedIds.size} listas (CIPA)
            </DialogTitle>
            <DialogDescription>
              Para CIPA é necessário informar a gestão. O resultado serão 2 ZIPs:
              listas de presença e certificados (com DESIGNADO).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Gestão (ex.: 2026/2027) <span className="text-rose-600">*</span>
              </label>
              <Input
                value={bulkDlGestao}
                onChange={(e) => setBulkDlGestao(e.target.value)}
                placeholder="2026/2027"
                disabled={bulkDownloading}
                data-testid="bulk-dl-gestao"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Posto de Serviço (opcional — fallback)
              </label>
              <Input
                value={bulkDlPosto}
                onChange={(e) => setBulkDlPosto(e.target.value)}
                placeholder="Cada funcionário usa o próprio posto cadastrado"
                disabled={bulkDownloading}
                data-testid="bulk-dl-posto"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Supervisor que assina o DESIGNADO
              </label>
              <select
                value={bulkDlSupervisorId}
                onChange={(e) => setBulkDlSupervisorId(e.target.value)}
                disabled={bulkDownloading}
                className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="bulk-dl-supervisor"
              >
                <option value="">(default: Edmilson Aleluia)</option>
                {supervisorOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.matricula} — {s.nome}
                  </option>
                ))}
              </select>
            </div>
            {bulkDownloading && bulkDlStatus && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin inline" />
                {bulkDlStatus}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDlDialog(false)}
              disabled={bulkDownloading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkDownload}
              disabled={bulkDownloading || !bulkDlGestao.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-dl-confirm-btn"
            >
              {bulkDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar 2 ZIPs
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk e-mail modal: send multiple selected lists in one go */}
      <Dialog
        open={bulkDialog}
        onOpenChange={(o) => !bulkSending && setBulkDialog(o)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-700" />
              Enviar {selectedIds.size} listas por e-mail
            </DialogTitle>
            <DialogDescription>
              Cada lista selecionada será enviada como um e-mail separado
              (com lista de presença + certificados, ou ZIP da OS).
              Processamento sequencial.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-semibold text-slate-700">
                Para <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="destinatario@example.com"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="bulk-email-to"
                disabled={bulkSending}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">
                CC <span className="text-slate-400">(opcional)</span>
              </label>
              <input
                type="text"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="cc1@example.com, cc2@example.com"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="bulk-email-cc"
                disabled={bulkSending}
              />
            </div>
            {course?.course_key === "cipa" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Posto (opcional, fallback)
                  </label>
                  <input
                    type="text"
                    value={emailPosto}
                    onChange={(e) => setEmailPosto(e.target.value)}
                    placeholder="Em branco = usa o do funcionário"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase"
                    disabled={bulkSending}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Gestão <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailGestao}
                    onChange={(e) => setEmailGestao(e.target.value)}
                    placeholder="Ex: 2026/2027"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    disabled={bulkSending}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Supervisor do DESIGNADO
                  </label>
                  <select
                    value={emailSupervisorId}
                    onChange={(e) => setEmailSupervisorId(e.target.value)}
                    disabled={bulkSending || !supervisorOptions.length}
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white"
                  >
                    <option value="">Padrão (Edmilson Aleluia)</option>
                    {supervisorOptions.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.matricula} — {sup.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {bulkProgress && (
              <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-xs text-blue-800">
                Enviando {bulkProgress.current}/{bulkProgress.total}…
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDialog(false)}
              disabled={bulkSending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkEmail}
              disabled={bulkSending || !emailTo.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bulk-email-send-btn"
            >
              {bulkSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar {selectedIds.size} e-mail(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={emailDialog.open}
        onOpenChange={(o) =>
          !sendingEmail && setEmailDialog({ open: o, list: o ? emailDialog.list : null })
        }
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-700" /> Enviar por email
            </DialogTitle>
            <DialogDescription>
              Lista de presença (PDF) + certificados (ZIP) serão anexados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold text-slate-700">
                Destinatário(s) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="email1@x.com, email2@y.com"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="email-to-input"
                disabled={sendingEmail}
              />
              <p className="text-[10px] text-slate-500 mt-0.5">
                Separe múltiplos emails por vírgula.
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">
                Cópia (CC)
              </label>
              <input
                type="text"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="email3@z.com (opcional)"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="email-cc-input"
                disabled={sendingEmail}
              />
            </div>
            {course?.course_key === "cipa" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Posto de Serviço <span className="text-slate-400 font-normal">(opcional — fallback)</span>
                  </label>
                  <input
                    type="text"
                    value={emailPosto}
                    onChange={(e) => setEmailPosto(e.target.value)}
                    placeholder="Deixe em branco para usar o posto de cada funcionário"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm uppercase"
                    data-testid="email-posto-input"
                    disabled={sendingEmail}
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Cada DESIGNADO usará automaticamente o posto cadastrado no funcionário.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Gestão <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailGestao}
                    onChange={(e) => setEmailGestao(e.target.value)}
                    placeholder="Ex: 2025/2026"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="email-gestao-input"
                    disabled={sendingEmail}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Supervisor do DESIGNADO
                  </label>
                  <select
                    value={emailSupervisorId}
                    onChange={(e) => setEmailSupervisorId(e.target.value)}
                    disabled={sendingEmail || !supervisorOptions.length}
                    data-testid="email-supervisor-select"
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">
                      Padrão (Edmilson Aleluia Borges Dos Santos)
                    </option>
                    {supervisorOptions.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.matricula} — {sup.nome}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Esse supervisor único assinará TODOS os DESIGNADOS desta emissão.
                  </p>
                </div>
              </>
            )}
            <div className="text-[10px] text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
              <strong>Assunto:</strong>{" "}
              {course?.course_key === "ordem_servico"
                ? "ORDENS DE SERVIÇO"
                : "LISTA E CERTIFICADOS"}
              <br />
              <strong>De:</strong> AVI SEGURANÇA &lt;segurancadotrabalho@grupoavi.com.br&gt;
              {course?.course_key === "ordem_servico" && (
                <>
                  <br />
                  <span className="text-amber-700">
                    Listas grandes (centenas de funcionários) podem demorar
                    alguns minutos — o email é enviado em segundo plano.
                  </span>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailDialog({ open: false, list: null })}
              disabled={sendingEmail}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* -------------------- Main tab -------------------- */
const AdminCoursesTab = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genDialog, setGenDialog] = useState({ open: false, course: null });
  const [histDialog, setHistDialog] = useState({ open: false, course: null });
  const [certPrompt, setCertPrompt] = useState({
    open: false,
    course: null,
    list: null,
  });
  const [generatingCerts, setGeneratingCerts] = useState(false);
  const [faceReviewOpen, setFaceReviewOpen] = useState(false);
  const [facePendingCount, setFacePendingCount] = useState(0);

  const fetchFacePending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/face-reviews`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { status: "review" },
      });
      setFacePendingCount(r.data?.pending_count || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchFacePending(); }, [fetchFacePending]);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/courses`, { timeout: 15000 });
      setCourses(res.data || []);
    } catch (e) {
      toast.error("Erro ao carregar cursos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleListCreated = (_createdList) => {
    // Just refresh the course counters — don't open the certificate
    // prompt nor the history dialog (user emits via the new 10-slot
    // flow which already shows per-slot status; certs are downloaded
    // later from the history tab in bulk).
    fetchCourses();
  };

  // CIPA-specific dialog state for posto + gestão prompt before emission.
  const [cipaPrompt, setCipaPrompt] = useState({
    open: false, list: null, course: null, posto: "", gestao: "",
    supervisorId: "",
    fromHistory: false,
  });
  // Cached list of employees with signature for the supervisor dropdown.
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  useEffect(() => {
    if (!cipaPrompt.open) return;
    if (supervisorOptions.length) return;
    axios
      .get(`${API}/admin/employees-for-list`)
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : r.data?.items || [];
        const withSig = items
          .filter((e) => e.has_signature)
          .map((e) => ({ id: e.id, nome: e.nome, matricula: e.matricula }));
        setSupervisorOptions(withSig);
      })
      .catch(() => setSupervisorOptions([]));
  }, [cipaPrompt.open, supervisorOptions.length]);

  // CIPA supervisor digital signature (OTP via SMS) status for the list.
  const [supSig, setSupSig] = useState(null);
  const [sendingSupLink, setSendingSupLink] = useState(false);
  const [desigSig, setDesigSig] = useState(null);
  const [sendingDesigLinks, setSendingDesigLinks] = useState(false);
  useEffect(() => {
    if (!cipaPrompt.open || !cipaPrompt.list) {
      setSupSig(null);
      setDesigSig(null);
      return;
    }
    axios
      .get(`${API}/admin/courses/lists/${cipaPrompt.list.id}/supervisor-signature`)
      .then((r) => setSupSig(r.data))
      .catch(() => setSupSig(null));
    axios
      .get(`${API}/admin/courses/lists/${cipaPrompt.list.id}/designado-signatures`)
      .then((r) => setDesigSig(r.data))
      .catch(() => setDesigSig(null));
  }, [cipaPrompt.open, cipaPrompt.list]);

  const canalLabel = (c) =>
    ({ whatsapp: "WhatsApp", sms: "SMS", email: "e-mail",
       portal: "aviso no portal do funcionário" }[c] || c);

  const handleSendSupervisorLink = async () => {
    if (!cipaPrompt.list) return;
    setSendingSupLink(true);
    try {
      const r = await axios.post(
        `${API}/admin/courses/lists/${cipaPrompt.list.id}/supervisor-signature/request`,
        { supervisor_id: cipaPrompt.supervisorId || null },
      );
      toast.success(
        `Link de assinatura enviado via ${canalLabel(r.data.canal)} para ${r.data.supervisor_nome} (${r.data.telefone_masked})`,
      );
      setSupSig({
        status: "pending",
        supervisor_nome: r.data.supervisor_nome,
        telefone_masked: r.data.telefone_masked,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar o link por SMS");
    }
    setSendingSupLink(false);
  };

  const handleSendDesignadoLinks = async () => {
    if (!cipaPrompt.list) return;
    setSendingDesigLinks(true);
    try {
      const r = await axios.post(
        `${API}/admin/courses/lists/${cipaPrompt.list.id}/designado-signatures/request`,
      );
      const parts = [];
      (r.data.enviados || []).forEach((e) =>
        parts.push(`${e.nome}: ${canalLabel(e.canal)}`));
      if (r.data.ja_assinados) parts.push(`${r.data.ja_assinados} já assinado(s)`);
      if (r.data.sem_contato?.length) parts.push(`${r.data.sem_contato.length} sem contato (verão no portal)`);
      toast.success(parts.join(" · ") || "Nada a enviar");
      const st = await axios.get(
        `${API}/admin/courses/lists/${cipaPrompt.list.id}/designado-signatures`,
      );
      setDesigSig(st.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar os links por SMS");
    }
    setSendingDesigLinks(false);
  };

  const triggerCertEmission = async (list, course, extraQuery = "") => {
    try {
      const { certFormat } = await runCertJobAndDownload(
        list.id, course.course_key, extraQuery,
      );
      if (certFormat === "docx") {
        toast.warning(
          "⚠️ Certificados saíram como .DOCX (conversor PDF indisponível no servidor). Contate o suporte.",
          { duration: 12000 }
        );
      } else {
        toast.success("Certificados gerados");
      }
      return true;
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Erro ao gerar certificados";
      toast.error(detail);
      return false;
    }
  };

  const handleConfirmEmitCertificates = async () => {
    if (!certPrompt.list || !certPrompt.course) return;
    const c = certPrompt.course;
    if (c.course_key === "cipa") {
      // Hand off to the CIPA prompt (posto + gestão) before downloading.
      setCipaPrompt({
        open: true, list: certPrompt.list, course: c,
        posto: "", gestao: "", fromHistory: false,
      });
      setCertPrompt({ open: false, course: null, list: null });
      return;
    }
    setGeneratingCerts(true);
    const ok = await triggerCertEmission(certPrompt.list, c);
    setGeneratingCerts(false);
    setCertPrompt({ open: false, course: null, list: null });
    if (ok) setHistDialog({ open: true, course: c });
  };

  const handleConfirmCipaEmission = async () => {
    const { list, course, posto, gestao, supervisorId, fromHistory } = cipaPrompt;
    if (!list || !course) return;
    if (!gestao.trim()) {
      toast.error("Preencha a gestão");
      return;
    }
    setGeneratingCerts(true);
    // Auto-dispara o link de assinatura (SMS) ao supervisor escolhido
    // quando ele ainda não recebeu nem assinou.
    if (supSig?.status === "none" || (!supSig && supervisorId)) {
      try {
        const r = await axios.post(
          `${API}/admin/courses/lists/${list.id}/supervisor-signature/request`,
          { supervisor_id: supervisorId || null },
        );
        toast.info(
          `Link de assinatura enviado via ${canalLabel(r.data.canal)} para ${r.data.supervisor_nome} (${r.data.telefone_masked})`,
        );
        setSupSig({
          status: "pending",
          supervisor_nome: r.data.supervisor_nome,
          telefone_masked: r.data.telefone_masked,
        });
      } catch (e) {
        toast.error(
          e?.response?.data?.detail ||
            "Falha ao enviar o link de assinatura ao supervisor",
        );
      }
    }
    let qs = `?gestao=${encodeURIComponent(gestao.trim())}`;
    if (posto.trim()) qs += `&posto=${encodeURIComponent(posto.trim())}`;
    if (supervisorId) qs += `&supervisor_id=${encodeURIComponent(supervisorId)}`;
    const ok = await triggerCertEmission(list, course, qs);
    setGeneratingCerts(false);
    setCipaPrompt({
      open: false, list: null, course: null, posto: "", gestao: "",
      supervisorId: "", fromHistory: false,
    });
    if (ok && !fromHistory) {
      setHistDialog({ open: true, course });
    }
  };

  const handleSkipCertificates = () => {
    const c = certPrompt.course;
    setCertPrompt({ open: false, course: null, list: null });
    if (c) setHistDialog({ open: true, course: c });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">
            Listas de Presença
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            6 cursos disponíveis. Selecione "Gerar Lista", escolha os
            funcionários e o sistema preenche automaticamente com a rúbrica de
            cada um.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFaceReviewOpen(true)}
            data-testid="admin-face-review-btn"
            className={facePendingCount > 0 ? "border-amber-400 text-amber-700 hover:bg-amber-50" : ""}
          >
            <ScanFace className="w-4 h-4 mr-1" />
            Revisão Facial
            {facePendingCount > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5">
                {facePendingCount}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCourses}
            data-testid="admin-courses-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Atualizar
          </Button>
        </div>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="admin-courses-grid"
      >
        {courses.map((c) => (
          <CourseCard
            key={c.course_key}
            course={c}
            onGenerate={(course) => setGenDialog({ open: true, course })}
            onHistory={(course) => setHistDialog({ open: true, course })}
          />
        ))}
      </div>

      <GenerateListDialog
        open={genDialog.open}
        onOpenChange={(open) =>
          setGenDialog({ open, course: open ? genDialog.course : null })
        }
        course={genDialog.course}
        onCreated={handleListCreated}
      />

      <FaceReviewDialog
        open={faceReviewOpen}
        onOpenChange={setFaceReviewOpen}
        onResolved={fetchFacePending}
      />

      <HistoryDialog
        open={histDialog.open}
        onOpenChange={(open) =>
          setHistDialog({ open, course: open ? histDialog.course : null })
        }
        course={histDialog.course}
        refreshCounters={fetchCourses}
        onRequestCipaEmit={(list) => {
          setCipaPrompt({
            open: true,
            list,
            course: histDialog.course,
            posto: "",
            gestao: "",
            fromHistory: true,
          });
        }}
      />

      {/* Certificate emission prompt after creating a list */}
      <Dialog
        open={certPrompt.open}
        onOpenChange={(open) =>
          !open && handleSkipCertificates()
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-700" />
              Emitir certificados?
            </DialogTitle>
            <DialogDescription>
              A lista de <strong>{certPrompt.course?.titulo}</strong> foi
              criada com{" "}
              <strong>{certPrompt.list?.count}</strong> participante(s).
              <br />
              Deseja emitir agora os certificados (PDF) para todos os
              funcionários da lista? Será gerado um arquivo .zip com um
              certificado por pessoa, nomeado pelo nome do funcionário.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleSkipCertificates}
              disabled={generatingCerts}
              data-testid="cert-prompt-skip-btn"
            >
              Não, agora não
            </Button>
            <Button
              onClick={handleConfirmEmitCertificates}
              disabled={generatingCerts}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="cert-prompt-confirm-btn"
            >
              {generatingCerts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-2" />
                  Sim, emitir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* CIPA-only: posto + gestão prompt */}
      <Dialog
        open={cipaPrompt.open}
        onOpenChange={(open) =>
          !open &&
          !generatingCerts &&
          setCipaPrompt({
            open: false, list: null, course: null, posto: "", gestao: "",
            fromHistory: false,
          })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-700" />
              Emitir certificados — CIPA
            </DialogTitle>
            <DialogDescription>
              Para emitir os certificados da CIPA, informe a{" "}
              <strong>gestão</strong>. O posto é opcional — cada DESIGNADO
              usa o posto cadastrado no funcionário automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Posto de serviço <span className="text-slate-400">(opcional — fallback)</span>
              </label>
              <Input
                value={cipaPrompt.posto}
                onChange={(e) =>
                  setCipaPrompt((p) => ({ ...p, posto: e.target.value }))
                }
                placeholder="Deixe em branco para usar o posto de cada funcionário"
                disabled={generatingCerts}
                data-testid="cipa-prompt-posto"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Gestão
              </label>
              <Input
                value={cipaPrompt.gestao}
                onChange={(e) =>
                  setCipaPrompt((p) => ({ ...p, gestao: e.target.value }))
                }
                placeholder="Ex: 2026/2027"
                disabled={generatingCerts}
                data-testid="cipa-prompt-gestao"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Supervisor do Contrato
              </label>
              <div
                className="w-full h-10 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700"
                data-testid="cipa-prompt-supervisor"
              >
                Diego Sousa Pinho
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Supervisor fixo — assina digitalmente TODOS os DESIGNADOS desta emissão.
              </p>
            </div>
            {/* Assinatura digital obrigatória do supervisor (OTP via SMS) */}
            {supSig?.status === "signed" ? (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5"
                data-testid="cipa-supervisor-signed-box"
              >
                <p className="text-xs font-semibold text-emerald-800">
                  ✓ Supervisor assinou digitalmente
                </p>
                <p className="text-[11px] text-emerald-700 mt-0.5">
                  {supSig.supervisor_nome}
                  {supSig.signed_at
                    ? ` — em ${new Date(supSig.signed_at).toLocaleString("pt-BR")}`
                    : ""}
                  . As cartas DESIGNADO sairão no ZIP com o carimbo digital.
                </p>
              </div>
            ) : (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-2"
                data-testid="cipa-supervisor-pending-box"
              >
                <p className="text-xs font-semibold text-amber-800">
                  Assinatura digital do supervisor pendente
                </p>
                <p className="text-[11px] text-amber-700">
                  {supSig?.status === "pending"
                    ? `Link já enviado para ${supSig.supervisor_nome || "o supervisor"} (${supSig.telefone_masked || "-"}). Você pode reenviar abaixo.`
                    : "Escolha o supervisor acima e clique em Emitir — o link de assinatura (SMS) será enviado automaticamente. Sem a assinatura, o ZIP sai apenas com os certificados. Você também pode enviar agora:"}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100 h-8"
                  onClick={handleSendSupervisorLink}
                  disabled={sendingSupLink || generatingCerts}
                  data-testid="cipa-supervisor-send-link-btn"
                >
                  {sendingSupLink ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Enviando SMS...
                    </>
                  ) : supSig?.status === "pending" ? (
                    "Reenviar link de assinatura (WhatsApp/SMS)"
                  ) : (
                    "Enviar link de assinatura ao supervisor (WhatsApp/SMS)"
                  )}
                </Button>
              </div>
            )}
            {/* Assinatura digital dos designados (OTP via SMS) */}
            {desigSig && (
              desigSig.assinados >= desigSig.total && desigSig.total > 0 ? (
                <div
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5"
                  data-testid="cipa-designados-signed-box"
                >
                  <p className="text-xs font-semibold text-emerald-800">
                    ✓ Designados: {desigSig.assinados}/{desigSig.total} assinaram digitalmente
                  </p>
                </div>
              ) : (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-2"
                  data-testid="cipa-designados-pending-box"
                >
                  <p className="text-xs font-semibold text-amber-800">
                    Designados: {desigSig.assinados}/{desigSig.total} assinaram digitalmente
                  </p>
                  <p className="text-[11px] text-amber-700">
                    Cada designado precisa assinar via OTP (SMS) para a sua
                    carta entrar no ZIP. Pendentes:{" "}
                    {desigSig.items
                      .filter((i) => i.status !== "signed")
                      .map((i) => i.nome)
                      .join(", ") || "-"}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-300 text-amber-800 hover:bg-amber-100 h-8"
                    onClick={handleSendDesignadoLinks}
                    disabled={sendingDesigLinks || generatingCerts}
                    data-testid="cipa-designados-send-links-btn"
                  >
                    {sendingDesigLinks ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Enviando SMS...
                      </>
                    ) : (
                      "Enviar links de assinatura aos designados (WhatsApp/SMS)"
                    )}
                  </Button>
                </div>
              )
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setCipaPrompt({
                  open: false, list: null, course: null, posto: "",
                  gestao: "", supervisorId: "", fromHistory: false,
                })
              }
              disabled={generatingCerts}
              data-testid="cipa-prompt-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmCipaEmission}
              disabled={
                generatingCerts ||
                !cipaPrompt.gestao.trim()
              }
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="cipa-prompt-confirm-btn"
            >
              {generatingCerts ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Award className="w-4 h-4 mr-2" />
                  Emitir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoursesTab;
